class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('[data-predictive-search-input]');
    this.results = this.querySelector('[data-predictive-search-results]');
    this.debounceTimer = null;
    this.cache = new Map();

    if (this.input && this.results) {
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim().length >= 2) this.onInput();
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.hideResults();
      });
    }
  }

  onInput() {
    const query = this.input.value.trim();
    if (query.length < 2) {
      this.hideResults();
      return;
    }
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.fetchSuggestions(query), 200);
  }

  async fetchSuggestions(query) {
    if (this.cache.has(query)) {
      this.renderSuggestions(this.cache.get(query));
      return;
    }
    try {
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,article,page&resources[limit]=6`);
      if (!response.ok) return;
      const data = await response.json();
      this.cache.set(query, data);
      this.renderSuggestions(data);
    } catch (err) {
      console.error('Predictive search failed', err);
    }
  }

  renderSuggestions(data) {
    const products = data?.resources?.results?.products || [];
    const articles = data?.resources?.results?.articles || [];
    const pages = data?.resources?.results?.pages || [];

    if (!products.length && !articles.length && !pages.length) {
      this.results.innerHTML = '<p class="search-bar__results-empty">geen resultaten</p>';
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      return;
    }

    const renderRow = (item, subtitle) => {
      const image = item.image
        ? `<img src="${this.resizeImage(item.image, 96)}" alt="${this.escape(item.title)}" width="48" height="48" loading="lazy" class="h-12 w-12 rounded-md object-cover">`
        : '<div class="h-12 w-12 rounded-md" style="background-color: var(--color-muted);"></div>';
      const price = item.price ? `<span class="text-xs font-semibold" style="color: var(--color-primary);">${item.price}</span>` : '';
      return `
        <a href="${item.url}" class="flex items-center gap-3 rounded-lg p-2 hover:bg-zinc-100" role="option">
          ${image}
          <div class="flex flex-1 flex-col gap-0.5">
            <span class="text-sm font-medium lowercase text-zinc-800 line-clamp-1">${this.escape(item.title)}</span>
            ${price ? price : `<span class="text-xs text-zinc-500 lowercase">${subtitle}</span>`}
          </div>
        </a>
      `;
    };

    const sections = [];
    if (products.length) sections.push('<div class="flex flex-col gap-1">' + products.map((p) => renderRow(p, 'product')).join('') + '</div>');
    if (articles.length) sections.push('<div class="mt-2 flex flex-col gap-1 border-t pt-2" style="border-color: var(--color-border);">' + articles.map((a) => renderRow(a, 'artikel')).join('') + '</div>');
    if (pages.length) sections.push('<div class="mt-2 flex flex-col gap-1 border-t pt-2" style="border-color: var(--color-border);">' + pages.map((p) => renderRow(p, 'pagina')).join('') + '</div>');

    this.results.innerHTML = sections.join('');
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  hideResults() {
    if (!this.results) return;
    this.results.hidden = true;
    this.input?.setAttribute('aria-expanded', 'false');
  }

  resizeImage(url, size) {
    if (!url) return url;
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', size);
      parsed.searchParams.set('height', size);
      parsed.searchParams.set('crop', 'center');
      return parsed.toString();
    } catch (err) {
      return url;
    }
  }

  escape(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
